import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Context
import { boardTemplatePickerContext, BoardTemplatePickerContextProvider } from './context/boardTemplatePickerContext';

// Components
import SheetContainer from '../../mobile/structural/sheet';
import BoardTemplatePickerCopyLoader from './loader/BoardTemplatePickerCopyLoader';
import MobileBoardTemplatePickerSheetContent from './MobileBoardTemplatePickerSheetContent';
import FloatingPanel from '../../components/tooltips/FloatingPanel';
import getGridSize from '../../utils/grid/gridSizeSelector';

// Hooks
import { useCmsTemplateFetching } from './hooks/useCmsTemplateFetching';
import useSheet, { UseSheetReturnType } from '../../mobile/structural/sheet/hooks/useSheet';
import useTemplateZoomHandler from './hooks/useTemplateZoomHandler';
import useShouldRenderTemplatePicker from './hooks/useShouldRenderTemplatePicker';
import useOnboardingSelectTemplateHandler from './hooks/useOnboardingSelectTemplateHandler';

// Constants
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { SheetId } from '../../mobile/structural/sheet/sheetTypes';
import { TooltipPositions } from '../../components/tooltips/tooltipConstants';
import { OnboardingStepsAddTemplateMobile } from '../onboarding/config/onboardingStepsAddTemplateMobile';
import { OnboardingStepsAddTemplate } from '../onboarding/config/onboardingStepsAddTemplate';

// Utils
import { getIsSheetOpen } from '../../mobile/structural/sheet/sheetSelectors';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { selectIsCurrentToolbarTypeMobileTabs } from '../toolbar/selector/currentToolbarSelector';
import { onboardingStepPassed } from '../onboarding/onboardingActions';
import { getCurrentOnboardingStepId } from '../onboarding/onboardingSelector';

// Styles
import './board-template-picker-content.scss';
import './MobileBoardTemplatePickerSheet.scss';

type Props = {
    currentBoard: ImMNElement;
    sheetProps: UseSheetReturnType;
};

const MobileBoardTemplatePickerSheet = ({ sheetProps, currentBoard }: Props) => {
    const gridSize = useSelector(getGridSize);
    const isInboxSheetOpen = useSelector(getIsSheetOpen(SheetId.Inbox));
    const isCurrentToolbarTypeMobileTabs = useSelector(selectIsCurrentToolbarTypeMobileTabs);
    const currentOnboardingStepId = useSelector(getCurrentOnboardingStepId);
    const hasCurrentOnboardingStep = currentOnboardingStepId && currentOnboardingStepId !== 'empty-step';

    const dispatch = useDispatch();

    useTemplateZoomHandler();

    const { templatePickerVisible, selectTemplate, isCopyingTemplate, prevSelectedTemplateIdRef } =
        useContext(boardTemplatePickerContext);

    // Automatically select a template when the user is in the template onboarding flow
    const onboardingSelectTemplateId = useOnboardingSelectTemplateHandler({
        beforeSelectTemplate: () => sheetProps.dispatchOpenSheet(),
        shouldAutoSelectTemplate:
            templatePickerVisible &&
            (currentOnboardingStepId === OnboardingStepsAddTemplateMobile.NEW_USER_3_WAIT_FOR_USE ||
                currentOnboardingStepId === OnboardingStepsAddTemplate.CREATE_BOARD_2_WAIT_FOR_USE),
    });

    useEffect(() => {
        if (!sheetProps.isSheetOpen) return;

        // When the sheet is closed and there is an onboarding state, we want to clear it
        return () => {
            if (!hasCurrentOnboardingStep) return;

            prevSelectedTemplateIdRef.current = null;
            dispatch(onboardingStepPassed('template-picker'));
        };
    }, [sheetProps.isSheetOpen]);

    // Close sheet when template picker should no longer be shown (e.g. when an element is created on the board)
    useEffect(() => {
        if (sheetProps.isSheetOpen && !templatePickerVisible) {
            sheetProps.dispatchCloseSheet();
        }
    }, [sheetProps.isSheetOpen, templatePickerVisible]);

    const shouldShowTriggerPanel =
        templatePickerVisible &&
        !isCopyingTemplate &&
        isCurrentToolbarTypeMobileTabs &&
        !sheetProps.isSheetOpen &&
        !isInboxSheetOpen &&
        // Don't show the trigger panel if the user is in an onboarding flow
        !hasCurrentOnboardingStep;

    return (
        <>
            <FloatingPanel
                className="MobileTemplatePickerTriggerPanel"
                selector="#create-element-popup-selector"
                mountPointSelector="#app .Workspace.workspace-ready"
                arrowWidth={13}
                arrowHeight={8}
                arrowStrokeWidth={0}
                distance={25}
                delay={1000}
                position={TooltipPositions.TOP}
                gridSize={gridSize}
                show={shouldShowTriggerPanel}
                pollPosition
                fade
            >
                <>
                    Tap + to add content or
                    <br />
                    <a onClick={() => sheetProps.dispatchOpenSheet()}>start with a template</a>
                </>
            </FloatingPanel>

            {isCopyingTemplate && <BoardTemplatePickerCopyLoader />}

            <SheetContainer
                {...sheetProps}
                className="MobileBoardTemplatePickerSheet"
                defaultSnapPoint={0.5}
                preventContentHeightChange
                onDragStart={() => {
                    // On drag start, we want to blur any active input element so that virtual keyboard will be hidden
                    if (document.activeElement?.tagName === 'INPUT') {
                        (document.activeElement as HTMLInputElement).blur();
                    }
                }}
                onCloseTransitionStart={() => {
                    selectTemplate(null);
                }}
            >
                <MobileBoardTemplatePickerSheetContent
                    currentBoard={currentBoard}
                    sheetProps={sheetProps}
                    shouldAutoSelectTemplate={
                        // The activeSnapPoint condition is used here to ensure that the scrollExcessPadding
                        // has been applied before selecting the template and zooming to fit to it's contents,
                        // which uses the scrollExcessPadding in its calculations.
                        sheetProps.activeSnapPoint > 0 &&
                        // Don't auto select previously selected template or first template if the user is
                        // in the onboarding flow
                        !onboardingSelectTemplateId
                    }
                />
            </SheetContainer>
        </>
    );
};

const MobileBoardTemplatePickerSheetContainer = ({ currentBoard }: Props) => {
    const sheetProps = useSheet(SheetId.TemplatePicker);
    const currentBoardId = getElementId(currentBoard);

    useCmsTemplateFetching();

    const shouldRenderTemplatePicker = useShouldRenderTemplatePicker(currentBoardId);
    if (!shouldRenderTemplatePicker) return null;

    return (
        <BoardTemplatePickerContextProvider key={currentBoardId} enabled={sheetProps.isSheetOpen}>
            <MobileBoardTemplatePickerSheet currentBoard={currentBoard} sheetProps={sheetProps} />
        </BoardTemplatePickerContextProvider>
    );
};

export default MobileBoardTemplatePickerSheetContainer;
